import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

// Manages the list of parts for a Series event.
// - Adds rows from a <template>, replacing `__INDEX__` placeholders in
//   field names so Rails can build the nested attributes correctly.
// - Removes rows on click.
// - Renumbers the "Part N" labels on every add/remove so the displayed
//   ordering always matches DOM order.
// - Keeps each title's placeholder showing the name the part will actually get
//   ("Job readiness - Part 2"), following the event name as it is typed on the
//   step before. A placeholder, not a value: an empty title means "use the
//   event name", so a later rename carries, and there is nothing stale to
//   clear out of the box.
//
// Usage:
//   <div data-controller="series-parts-editor"
//        data-series-parts-editor-index-value="1">
//     <div data-series-parts-editor-target="rows">…existing rows…</div>
//     <template data-series-parts-editor-target="template">
//       <!-- row markup with `__INDEX__` placeholder in field names -->
//     </template>
//     <button data-action="click->series-parts-editor#add">Add</button>
//   </div>
const NAME_FIELD = "event[name]"

export default class extends Controller {
  static targets = ["rows", "template", "number", "row", "title"]
  static values = { index: Number }

  declare readonly rowsTarget: HTMLElement
  declare readonly hasRowsTarget: boolean
  declare readonly templateTarget: HTMLTemplateElement
  declare readonly hasTemplateTarget: boolean
  declare readonly rowTargets: HTMLElement[]
  declare indexValue: number

  private onFormInput = (event: Event) => {
    const target = event.target as HTMLInputElement | null
    if (target?.name === NAME_FIELD) this.renumber()
  }

  connect() {
    this.renumber()
    // The name lives on an earlier step, outside this controller's element, so
    // the listener goes on the shared form rather than on a target here.
    this.element.closest("form")?.addEventListener("input", this.onFormInput)
  }

  disconnect() {
    this.element.closest("form")?.removeEventListener("input", this.onFormInput)
  }

  add(event: Event) {
    event.preventDefault()
    if (!this.hasTemplateTarget || !this.hasRowsTarget) return

    const html = this.templateTarget.innerHTML.replace(/__INDEX__/g, String(this.indexValue))
    this.indexValue = this.indexValue + 1

    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const node = wrapper.firstElementChild
    if (node) {
      this.rowsTarget.appendChild(node)
      this.renumber()
    }
  }

  remove(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const row = button.closest<HTMLElement>("[data-series-parts-editor-target='row']")
    if (row) {
      row.remove()
      this.renumber()
    }
  }

  private renumber() {
    const eventName = this.eventName()

    this.rowTargets.forEach((row, idx) => {
      const label = i18n.t("series_parts.part_number", { number: idx + 1 })

      const numberEl = row.querySelector<HTMLElement>(
        "[data-series-parts-editor-target='number']"
      )
      if (numberEl) numberEl.textContent = label

      const titleEl = row.querySelector<HTMLInputElement>(
        "[data-series-parts-editor-target='title']"
      )
      if (titleEl) titleEl.placeholder = eventName ? `${eventName} - ${label}` : label
    })
  }

  private eventName(): string {
    const field = this.element
      .closest("form")
      ?.querySelector<HTMLInputElement>(`[name="${NAME_FIELD}"]`)
    return field?.value.trim() ?? ""
  }
}
